/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { isString } from "../../../types/is.mjs";
import { fireCustomEvent } from "../../../dom/events.mjs";
import { validateInstance, validateString } from "../../../types/validate.mjs";

function findShadowRoot(element) {
	if (element instanceof ShadowRoot) return element;
	if (!element.parentNode) return null;
	return findShadowRoot(element.parentNode);
}

/**
 * @private
 * @param {HTMLElement} element
 * @param {string|URL} url
 * @param {Object} options fetch options
 * @param {Object} filter fetch options
 * @return {Promise<Object>}
 * @throws {Error} we won't be able to read the data
 * @throws {Error} client error
 * @throws {Error} undefined status or type
 * @throws {TypeError} value is not an instance of
 * @throws {TypeError} value is not a string
 */
function loadAndAssignContent(element, url, options, filter) {
	return loadContent(url, options).then((response) => {
		let content = response.content;

		if (isString(filter) && filter !== "") {
			const t = document.createElement("div");
			const c = document.createElement("div");
			c.innerHTML = content;
			for (const [, node] of c.querySelectorAll(filter).entries()) {
				t.appendChild(node);
			}

			content = t.innerHTML;
		}

		const t = document.createElement("div");
		t.innerHTML = content;

		const scripts = t.querySelectorAll("script");
		for (const [, script] of scripts.entries()) {
			const s = document.createElement("script");
			s.innerHTML = script.innerHTML;
			if (script.src) s.src = script.src;
			if (script.type) s.type = script.type;
			if (script.async) s.async = script.async;
			if (script.defer) s.defer = script.defer;
			if (script.crossOrigin) s.crossOrigin = script.crossOrigin;
			if (script.integrity) s.integrity = script.integrity;
			if (script.referrerPolicy) s.referrerPolicy = script.referrerPolicy;
			document.head.appendChild(s);
			t.removeChild(script);
		}

		validateInstance(element, HTMLElement).innerHTML = t.innerHTML;

		const root = findShadowRoot(element);
		if (root !== null) {
			element = root.host;
		}

		fireCustomEvent(element, "monster-fetched", {
			url,
		});

		return response;
	});
}

/**
 * @private
 * @param {string|URL} url
 * @param {Object} options fetch options
 * @return {Promise<string>}
 * @throws {Error} we won't be able to read the data
 * @throws {Error} client error
 * @throws {Error} undefined status or type
 * @throws {TypeError} value is not a string
 */
function loadContent(url, options) {
	if (url instanceof URL) {
		url = url.toString();
	}

	return fetch(validateString(url), options).then((response) => {
		// The ok read-only property of the Response interface contains a
		// Boolean stating whether the response was successful (status in the range 200-299) or not.
		if (response?.ok !== true) {
			// @see https://developer.mozilla.org/en-US/docs/Web/API/Response/type
			if (
				["error", "opaque", "opaqueredirect"].indexOf(response?.type) !== -1
			) {
				throw new Error(
					`we won't be able to read the data (${response?.type})`,
				);
			}

			const statusClass = `${response?.status}`.substring(0, 1);
			switch (statusClass) {
				case "4":
					throw new Error(`client error ${response?.statusText}`);
					break;
				default:
					throw new Error(
						`undefined status (${response?.status} / ${response?.statusText}) or type (${response?.type})`,
					);
			}
		}

		return new Promise(function (resolve, reject) {
			response
				.text()
				.then((content) => {
					resolve({
						content,
						type: response.headers.get("Content-Type"),
					});
				})
				.catch(reject);
		});
	});
}

export { loadAndAssignContent, loadContent };
